// Client REST typé : chaque chemin, paramètre et réponse vient de schema.d.ts.
// L'authentification passe par le cookie de session (HttpOnly), envoyé automatiquement par le navigateur.
import createClient from "openapi-fetch";
import { getLang, N_, t } from "../lib/i18n";
import type { paths } from "./schema";

// Prévenu quand l'API répond 401 (session expirée ou révoquée) : l'application revient à l'écran de connexion.
const unauthorizedListeners = new Set<() => void>();
export function onUnauthorized(fn: () => void): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}
export function notifyUnauthorized(): void {
  unauthorizedListeners.forEach((fn) => fn());
}

export const api = createClient<paths>({ baseUrl: "", credentials: "same-origin" });
api.use({
  // messages d'erreur du serveur dans la langue de l'interface
  onRequest({ request }) {
    request.headers.set("X-Gateway-Lang", getLang());
    return request;
  },
  onResponse({ request, response }) {
    // un mauvais mot de passe sur /auth/login n'est pas une session perdue
    if (response.status === 401 && !new URL(request.url).pathname.startsWith("/api/auth/")) notifyUnauthorized();
    return response;
  },
});

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

const FIELD_LABELS: Record<string, string> = {
  id: N_("Identifiant de l'entrée"), name: N_("Nom"), mode: N_("Mode"), protocol: N_("Protocole"), source_url: N_("URL source"),
  stream_key: N_("Clé de flux"), srt_passphrase: N_("Passphrase SRT"), input_id: N_("Source"), url: N_("URL"),
  video_bitrate_kbps: N_("Débit vidéo"), audio_bitrate_kbps: N_("Débit audio"),
  backup_input_id: N_("Secours"), ndi_name: N_("Nom de la source NDI"), token: N_("Lien"), code: N_("Code"),
  current: N_("Mot de passe actuel"), first_name: N_("Prénom"), last_name: N_("Nom de famille"),
  email: N_("Adresse e-mail"), company: N_("Société"), usage: N_("Usage prévu"), phone: N_("Téléphone"),
  public_url: N_("Adresse de l'interface"), notify_to: N_("Adresse à prévenir"), smtp_host: N_("Serveur SMTP"),
  smtp_port: N_("Port"), mail_from: N_("Expéditeur"), mail_to: N_("Destinataires"), record_dir: N_("Dossier"),
  info_email: N_("Adresse pour les informations"), support_email: N_("Adresse du support"),
  hours: N_("Horaires (français)"), hours_en: N_("Horaires (anglais)"), max_inputs: N_("Entrées au maximum"),
  max_outputs: N_("Sorties au maximum"), discovery: N_("Discovery Server"), groups: N_("Groupes NDI"),
  machine_name: N_("Nom de la machine"), text: N_("Texte"), bot_token: N_("Jeton du bot"), chat_id: N_("Chat ID"),
  username: N_("Identifiant"), password: N_("Mot de passe"), new: N_("Nouveau mot de passe"), role: N_("Rôle"),
};

/** Message lisible à partir d'une erreur FastAPI ({detail: "..."} ou erreurs de validation). */
export function errorMessage(error: unknown, status?: number): string {
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: { loc?: (string | number)[]; msg?: string }) => {
        const field = d.loc?.filter((p) => p !== "body").at(-1);
        const msg = (d.msg ?? t("valeur invalide")).replace(/^Value error, /, "");
        const known = typeof field === "string" ? FIELD_LABELS[field] : undefined;
        const label = known ? t(known) : typeof field === "string" ? field : undefined;
        return label ? t("{label} : {msg}", { label, msg }) : msg;
      })
      .join(" · ");
  }
  if (status === 403) return t("Vos droits ne permettent pas cette action.");
  if (status === 502) return t("ServerMedia ne répond pas.");
  return status ? t("Erreur {status}", { status }) : t("Le serveur ne répond pas.");
}

/** Attend une réponse openapi-fetch et renvoie ses données, ou lève une ApiError lisible. */
export async function unwrap<T>(call: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let r: { data?: T; error?: unknown; response: Response };
  try {
    r = await call;
  } catch {
    throw new ApiError(t("Le serveur ne répond pas."), 0);
  }
  if (!r.response.ok) throw new ApiError(errorMessage(r.error, r.response.status), r.response.status);
  return r.data as T;
}
